'use client';

import { capitalizeWords, NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { memo } from 'react';
import { DEPOSIT_PAYMENT_METHODS } from '../../../../PaymentMethodSelector/PaymentMethods';
import { TransactionPod } from '../../../../TransactionPod/TransactionPod';
import type { WalletTransaction } from '../../types';

const DEPOSIT_PAYMENT_METHOD_API_NAMES = new Set(Object.values(DEPOSIT_PAYMENT_METHODS).map(method => method.apiName.toLowerCase()));

// Types
type TransactionItemProps = {
  namespace: string;
  className?: string;
  transaction: WalletTransaction;
};

// Helper function to get transaction title
function getTransactionTitle(type: string, t: (key: string) => string): string {
  const titleMap = {
    BAD_DEBT: 'bad_debt',
    BONUS: 'bonus',
    BONUS_CREDIT: 'bonus_credit',
    BONUS_DEACTIVATE: 'bonus_deactivate',
    BONUS_EXPIRE: 'bonus_expire',
    BONUS_GRANT: 'bonus_grant',
    BONUS_REDEEM: 'bonus_redeem',
    CANCEL: 'cancel',
    CREDIT_NOTE: 'credit_note',
    DERECOGNISE: 'derecognise',
    DEPOSIT: 'deposit',
    PAYIN: 'payin',
    PAYOUT: 'payout',
    STAKE: 'stake',
    TRANSFER: 'transfer',
    UNLOCK: 'unlock',
    WAGER: 'wager',
    WINNINGS: 'winnings',
    WITHDRAWAL: 'withdrawal',
  } as const;

  return t(titleMap[type as keyof typeof titleMap] || 'unknown');
}

// Helper function to map transaction type to TransactionPod type
function mapTransactionType(type: string, amountSymbol: string): 'WINNINGS' | 'WITHDRAWAL' | 'DEPOSIT' {
  if (type === 'WINNINGS') return 'WINNINGS';
  return amountSymbol === '-' ? 'WITHDRAWAL' : 'DEPOSIT';
}

type ProductTranslator = {
  (key: string): string;
  has: (key: string) => boolean;
};

type LotteryTranslator = ProductTranslator;

function getTransactionProductLabel(productName: string | undefined | null, t: ProductTranslator, tLotteries: LotteryTranslator): string {
  if (!productName) {
    return '';
  }
  if (DEPOSIT_PAYMENT_METHOD_API_NAMES.has(productName.toLowerCase())) {
    return t(`payment_methods.${productName.toLowerCase()}`);
  }
  const internalProductKey = `product_names.${productName}`;
  if (t.has(internalProductKey)) {
    return t(internalProductKey);
  }
  const lotteryNameKey = `${productName}.name`;
  if (tLotteries.has(lotteryNameKey)) {
    return tLotteries(lotteryNameKey);
  }
  return productName;
}

const TransactionItem = memo(function TransactionItem({ namespace, className, transaction }: TransactionItemProps) {
  const t = useTranslations(namespace);
  const tLotteries = useTranslations('lotteries');
  const formatCurrency = useCurrencyFormatter();
  const { date, productName, type, amount, balance } = transaction;

  const title = getTransactionTitle(type, t);
  const dateString = t('transaction_time', { date: new Date(date) });
  const transactionAmountTotal = (amount.wallet || 0) + (amount.bonus || 0);
  const amountSymbol = transactionAmountTotal >= 0 ? '+' : '-';
  const formattedAmount = formatCurrency(transactionAmountTotal).format(NumberFormats.WITH_DECIMALS).get().replace('-', '');
  const amountString = `${amountSymbol} ${formattedAmount}`;

  const formattedCashBalance = formatCurrency(balance.wallet || 0)
    .format(NumberFormats.WITH_DECIMALS)
    .get();
  const cashBalanceString = `${t('cash_balance')}: ${formattedCashBalance}`;

  const formattedBonusBalance = formatCurrency(balance.bonus || 0)
    .format(NumberFormats.WITH_DECIMALS)
    .get();
  const bonusBalanceString = `${t('bonus_balance')}: ${formattedBonusBalance}`;
  const productLabel = getTransactionProductLabel(productName, t, tLotteries);

  return (
    <TransactionPod
      type={mapTransactionType(type, amountSymbol)}
      title={title}
      amount={amountString}
      className={className}
      data-testid={`transaction-item-${type.toLowerCase()}`}
      data-track-name="Transaction"
      data-track-type={capitalizeWords(String(type).toLowerCase())}
    >
      <div className="flex gap-3 items-center justify-between">
        <p className="paragraph-s grow" data-testid="transaction-date">
          {dateString}
        </p>
        <p className="paragraph-s" data-testid="transaction-cash-balance">
          {cashBalanceString}
        </p>
      </div>
      <div className="flex gap-3 items-center justify-between">
        <p className="paragraph-s grow" data-testid="transaction-product-name">
          {productLabel}
        </p>
        <p className="paragraph-s" data-testid="transaction-bonus-balance">
          {bonusBalanceString}
        </p>
      </div>
    </TransactionPod>
  );
});

export { TransactionItem };
